import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { readPublicHistory, savePublicHistory } from "@/lib/public-history";

/** Mounted inside the lazy public surface: never acts on the outgoing/fallback page. */
export default function PublicRouteRecovery() {
  const { key, hash, pathname } = useLocation();
  const navigation = useNavigationType();
  useLayoutEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    let y = readPublicHistory(key)?.scrollY ?? 0;
    let restoring = true;
    const remember = () => { if (!restoring) { y = window.scrollY; savePublicHistory(key, { scrollY: y }); } };
    window.addEventListener("scroll", remember, { passive: true });
    const saved = navigation === "POP" ? readPublicHistory(key)?.scrollY : undefined;
    // Existing hash hooks own other pages; gallery owns fresh hash starts itself.
    const hashOwned = Boolean(hash) && !(pathname === "/our-work" && saved !== undefined);
    const active = document.activeElement;
    let cancelled = hashOwned;
    let frame = 0;
    const cancel = () => { cancelled = true; restoring = false; };
    const events = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
    events.forEach(event => window.addEventListener(event, cancel, { passive: true }));
    const started = performance.now();
    let stable = 0, lastHeight = 0;
    const recover = () => {
      if (cancelled) { restoring = false; return; }
      const surface = document.querySelector<HTMLElement>(`[data-public-entry="${CSS.escape(key)}"]`);
      const main = surface?.querySelector<HTMLElement>("main#main");
      const height = document.documentElement.scrollHeight;
      stable = height === lastHeight ? stable + 1 : 0; lastHeight = height;
      const target = saved ?? 0;
      // Wait for committed, visible content and enough layout for the saved range.
      if (!main || !main.getClientRects().length || !surface?.querySelector("h1") || stable < 3 || height - window.innerHeight < target - 2) {
        if (performance.now() - started < 2200) frame = requestAnimationFrame(recover);
        else restoring = false;
        return;
      }
      const focused = document.activeElement;
      // A form/hash/skip-link focus move takes priority over generic recovery.
      if (focused !== active && focused !== document.body && focused?.isConnected) { restoring = false; return; }
      window.scrollTo({ top: target, left: 0, behavior: "instant" as ScrollBehavior });
      if (!focused || focused === document.body || !focused.isConnected) {
        if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
        main.focus({ preventScroll: true });
      }
      y = window.scrollY; restoring = false; savePublicHistory(key, { scrollY: y });
    };
    const timer = window.setTimeout(() => { frame = requestAnimationFrame(recover); }, 140);
    return () => {
      window.clearTimeout(timer); cancelAnimationFrame(frame);
      window.removeEventListener("scroll", remember);
      events.forEach(event => window.removeEventListener(event, cancel));
      savePublicHistory(key, { scrollY: y });
      window.history.scrollRestoration = previousRestoration;
    };
  }, [key, hash, pathname, navigation]);
  return null;
}